import { useMsal } from "@azure/msal-react";
import { Button, Modal, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "@tanstack/react-router";
import { cloneElement, memo, type ReactElement, useState } from "react";
import { deleteChat } from "src/utils/deleteChats";
import type { ChatEntity } from "../db";
import { useChatId } from "../hooks/useChatId";

export const DeleteChatModal = memo(function DeleteChatModal({
    chat,
    children,
}: {
    chat: ChatEntity;
    children: ReactElement;
}) {
    const [opened, { open, close }] = useDisclosure(false);
    const [submitting, setSubmitting] = useState(false);

    const chatId = useChatId();
    const navigate = useNavigate();
    const { instance } = useMsal();

    return (
        <>
            {cloneElement(children, { onClick: open } as React.HTMLAttributes<HTMLElement>)}
            <Modal opened={opened} onClose={close} title="Delete Chat">
                <form
                    onSubmit={async (event) => {
                        try {
                            setSubmitting(true);
                            event.preventDefault();
                            await deleteChat(chat.id, chatId, instance, navigate);
                            close();

                            notifications.show({
                                title: "Deleted",
                                message: "Chat deleted.",
                            });
                        } catch (err) {
                            console.error("Failed to delete chat:", err);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    <Stack>
                        <Text size="sm">Are you sure you want to delete this chat?</Text>
                        <Button
                            data-testid="delete-chat-button"
                            type="submit"
                            color="red"
                            loading={submitting}
                        >
                            Delete
                        </Button>
                    </Stack>
                </form>
            </Modal>
        </>
    );
});
